import { motion } from 'framer-motion';
import { theme } from '@/lib/theme';
import { useAvailability } from '@/hooks/useAvailability';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import WeeklySchedule from './components/WeeklySchedule';
import BlockedSlotsList from './components/BlockedSlotsList';
import AvailabilityCalendar from './components/AvailabilityCalendar';

export default function AvailabilityPage() {
  const {
    availability,
    blockedSlots,
    loading,
    error,
    saveAvailabilityRow,
    addBlockedSlot,
    removeBlockedSlot,
    refetch,
  } = useAvailability();

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar
          title="Disponibilidad"
          subtitle="Gestiona tu horario y tiempos bloqueados"
        />

        <main className="flex-1 overflow-y-auto px-6 py-8">
          {/* Page header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-white text-xl font-semibold" style={{ fontFamily: theme.fonts.display }}>
                Gestión de Horario
              </h2>
              <p className="text-sm mt-1" style={{ color: theme.colors.textMuted }}>
                Configura tus horas de trabajo y bloquea tiempos no disponibles
              </p>
            </div>
            <button
              onClick={refetch}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors hover:bg-white/5"
              style={{ color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}` }}
            >
              <i className="ri-refresh-line text-sm" />
              Actualizar
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
              style={{ backgroundColor: theme.colors.dangerMuted, borderColor: 'rgba(248,113,113,0.2)' }}
            >
              <i className="ri-error-warning-line text-red-400" />
              <p className="text-red-400 text-sm flex-1">{error}</p>
              <button onClick={refetch} className="text-red-400 text-xs underline cursor-pointer whitespace-nowrap">
                Reintentar
              </button>
            </motion.div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: theme.colors.bgSurface }} />
                ))}
              </div>
              <div className="space-y-4">
                <div className="h-72 rounded-xl animate-pulse" style={{ backgroundColor: theme.colors.bgSurface }} />
                <div className="h-48 rounded-xl animate-pulse" style={{ backgroundColor: theme.colors.bgSurface }} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Weekly schedule */}
              <div className="lg:col-span-2 space-y-6">
                <div
                  className="rounded-xl border p-6"
                  style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: theme.colors.primaryMuted }}>
                      <i className="ri-time-line text-sm" style={{ color: theme.colors.primary }} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Horario Semanal</p>
                      <p className="text-xs" style={{ color: theme.colors.textMuted }}>Activa días y edita horas por día</p>
                    </div>
                  </div>
                  <WeeklySchedule availability={availability} onSave={saveAvailabilityRow} />
                </div>

                {/* Blocked slots */}
                <div
                  className="rounded-xl border p-6"
                  style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
                >
                  <BlockedSlotsList
                    blockedSlots={blockedSlots}
                    onAdd={addBlockedSlot}
                    onRemove={removeBlockedSlot}
                  />
                </div>
              </div>

              {/* Right: Calendar overview */}
              <div className="space-y-4">
                <div
                  className="rounded-xl border p-5"
                  style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: theme.colors.primaryMuted }}>
                      <i className="ri-calendar-line text-sm" style={{ color: theme.colors.primary }} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">Vista de Calendario</p>
                      <p className="text-xs" style={{ color: theme.colors.textMuted }}>Resumen de disponibilidad</p>
                    </div>
                  </div>
                  <AvailabilityCalendar availability={availability} blockedSlots={blockedSlots} />
                </div>

                {/* Quick stats */}
                <div
                  className="rounded-xl border p-5"
                  style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
                >
                  <p className="text-white text-sm font-semibold mb-4">Estadísticas Rápidas</p>
                  <div className="space-y-3">
                    {[
                      {
                        label: 'Días laborales/semana',
                        value: availability.filter((a) => a.is_working).length,
                        icon: 'ri-calendar-check-line',
                      },
                      {
                        label: 'Bloqueos próximos',
                        value: blockedSlots.length,
                        icon: 'ri-forbid-line',
                      },
                    ].map((stat) => (
                      <div key={stat.label} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${stat.icon} text-sm`} style={{ color: theme.colors.textMuted }} />
                          </div>
                          <span className="text-sm" style={{ color: theme.colors.textSecondary }}>{stat.label}</span>
                        </div>
                        <span className="text-sm font-semibold text-white">{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
